<script setup>
import { storeToRefs } from 'pinia'
import { onMounted, ref, watch } from 'vue'
import { useDetailMapHelpers } from '@/composables/useDetailMapHelpers'
import {
  DETAIL_INFRAS_MAP_FEATURE_LAYER,
  DETAIL_INFRAS_MAP_INFO_LAYERS,
  MAP_CENTER,
  MAP_EXTENT,
  MAP_MAXZOOM,
  MAP_MINZOOM,
  MAP_PROJECTION,
} from '@/constants'
import { createGeoJsonFeature } from '@/services/map/FeatureService'
import { transform } from '@/services/map/ProjectionService'
import { useTransactionStore } from '@/stores/transaction'
import { useDetailStore } from '@/stores/useDetailStore'
import { useInfraStore } from '@/stores/useInfraStore'
import MapComponent from '../../map/MapComponent.vue'
import PredefinedValuesInputComponent from '../../PredefinedValuesInputComponent.vue'
import DetailCard from '../DetailCard.vue'

const detailStore = useDetailStore()
const { setMapExport } = detailStore
const { landBoard } = storeToRefs(detailStore)

const infraStore = useInfraStore()
const { infra } = storeToRefs(infraStore)
const { saveInfra } = infraStore

// Auto-save infra data when any field changes
watch(infra, () => { saveInfra() }, { deep: true })

// RED-267: juurdepaas feeds the land kohandustabel master column — write
// through so an already-mounted table refreshes live.
watch(
  () => infra.value.juurdepaas,
  value => {
    const transactionStore = useTransactionStore()
    if (transactionStore.masterData) {
      transactionStore.masterData.juurdepaas = value
    }
  },
)

const { findLayer } = useDetailMapHelpers()

const map = ref(null)
const errorMessage = ref(null)
const loading = ref(true)
const categories = ref([
  'public_transportation_stop',
  'school',
  'kindergarden',
  'shop',
  // Perhaps soon:
  //'hospital',
  //'medical_center',
  //'beach',
  //'swimming_area',
  //'swimming_pool',
])
const type = ref(true)
const fields = ref({
  1: ['juurdepaas', 'parkimine_ymbruses'],
  2: ['konkureeriv_turupiirkond', 'kergliiklus'],
})

const mapview = ref({
  status: true,
  center: transform('fromLonLat', MAP_CENTER, MAP_PROJECTION),
  zoom: 3,
  extent: transform('extent', MAP_EXTENT, 'EPSG:4326', MAP_PROJECTION),
  bbox: null,
  minZoom: MAP_MINZOOM,
  maxZoom: MAP_MAXZOOM,
  projection: MAP_PROJECTION,
  baseLayers: [findLayer('ma-map')],
  infoLayers: DETAIL_INFRAS_MAP_INFO_LAYERS.map(layer => findLayer(layer)),
  overlay: null,
})

const createMapFeatures = geometry => {
  const layer = findLayer(DETAIL_INFRAS_MAP_FEATURE_LAYER)
  layer.features.push(createGeoJsonFeature(geometry, {}))
  mapview.value.overlay = layer
}

const loadClosestInfras = async (x, y) => {
  const promises = categories.value.map(category => {
    return infraStore.closestInfra({
      category,
      x,
      y,
      count: 2,
      type: type.value ? 1 : 0,
    })
  })
  await Promise.all(promises)
}

const onChange = () => {
  loading.value = true
  try {
    const landBoardData = landBoard.value
    if (landBoardData && landBoardData['x'] && landBoardData['y']) {
      loadClosestInfras(landBoardData['x'], landBoardData['y'])
    }
  } catch (error) {
    console.error('DetailInfraComponent: onChange error:', error)
    errorMessage.value = error
  } finally {
    loading.value = false
  }
}

const exportMap = async () => {
  if (!map.value) {
    return
  }

  let mapExport = ''
  if (infra.value) {
    mapExport = await map.value.exportMap()
  }

  setMapExport({ key: 'infra', value: mapExport })
}

onMounted(() => {
  loading.value = true
  try {
    if (landBoard.value && landBoard.value?.x && landBoard.value?.y) {
      loadClosestInfras(landBoard.value.x, landBoard.value.y)
    }

    if (landBoard.value?.geometry) {
      createMapFeatures(landBoard.value.geometry)
    }
    if (landBoard.value?.bbox) {
      mapview.value.bbox = landBoard.value.bbox
    }
  } catch (error) {
    console.error('DetailInfraComponent: Error loading land board data:', error)
    errorMessage.value = error
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <div>
    <DetailCard :loading="loading" :error="errorMessage">
      <template #heading>
        <span class="pi pi-map-marker"></span>
        {{ $t(`messages.detail.infra.title`) }}
      </template>
      <template #controls>
        <label v-if="!loading" for="detail-infra-switch" class="mb-0">
          {{ type ? $t(`messages.detail.infra.onRoads`) : $t(`messages.detail.infra.asCrowFlies`) }}
        </label>
        <ToggleSwitch
          v-if="!loading"
          id="detail-infra-switch"
          v-model="type"
          @change="onChange"
        />
      </template>

      <div id="detail-infra-map">
        <div class="detail-map">
          <MapComponent
            :center="mapview.center"
            :extent="mapview.extent"
            :zoom="mapview.zoom"
            :bbox="mapview.bbox"
            :minZoom="mapview.minZoom"
            :maxZoom="mapview.maxZoom"
            :projection="mapview.projection"
            :baseLayers="mapview.baseLayers"
            :infoLayers="mapview.infoLayers"
            :overlay="mapview.overlay"
            :zoomToFeatures="true"
            :zoomControl="true"
            :twoFingerPanZoom="true"
            @update="exportMap()"
            ref="map"
          />
        </div>
      </div>
      <div class="p-[20px]">
        <div class="grid grid-cols-2 gap-4 mb-2">
          <div class="col-span-1">
            <InputText
              fluid
              id="kauplus"
              v-model="infra.kauplus"
              :placeholder="$t(`messages.detail.infra.kauplus`)"
            />
          </div>
          <div class="col-span-1">
            <PredefinedValuesInputComponent
              category="infra"
              name="asukoha_kirjeldus"
              :append-on-select="true"
              v-model="infra['asukoha_kirjeldus']"
              :placeholder="$t(`messages.detail.infra.asukoha_kirjeldus`)"
            />
          </div>
        </div>
        <div class="grid grid-cols-2 gap-4 mb-4">
          <div class="col-span-1">
            <InputText
              fluid
              id="kool"
              v-model="infra.kool"
              :placeholder="$t(`messages.detail.infra.kool`)"
            />
          </div>
          <div class="col-span-1">
            <PredefinedValuesInputComponent
              category="infra"
              name="piirkonna_hoonestus"
              :append-on-select="true"
              v-model="infra.piirkonna_hoonestus"
              :placeholder="$t(`messages.detail.infra.piirkonna_hoonestus`)"
            />
          </div>
        </div>
        <div class="grid grid-cols-2 gap-4 mb-4">
          <div class="col-span-1">
            <InputText
              fluid
              id="lasteaed"
              v-model="infra.lasteaed"
              :placeholder="$t(`messages.detail.infra.lasteaed`)"
            />
          </div>
          <div class="col-span-1">
            <PredefinedValuesInputComponent
              category="infra"
              name="haljastus"
              :append-on-select="true"
              v-model="infra.haljastus"
              :placeholder="$t(`messages.detail.infra.haljastus`)"
            />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4 mb-4">
          <div class="col-span-1">
            <InputText
              fluid
              id="yhistransport"
              v-model="infra.yhistransport"
              :placeholder="$t(`messages.detail.infra.yhistransport`)"
            />
          </div>
          <div class="col-span-1">
            <PredefinedValuesInputComponent
              category="infra"
              name="veekogud_ymbruses"
              :append-on-select="true"
              v-model="infra.veekogud_ymbruses"
              :placeholder="$t(`messages.detail.infra.veekogud_ymbruses`)"
            />
          </div>
        </div>

        <template v-if="false">
          <div class="grid grid-cols-2 gap-4 mb-4">
            <div class="col-span-1">
              <InputText
                fluid
                id="haigla"
                v-model="infra.haigla"
                :placeholder="$t(`messages.detail.infra.haigla`)"
              />
            </div>
            <div class="col-span-1">
              <InputText
                fluid
                id="suurhaigla"
                v-model="infra.suurhaigla"
                :placeholder="$t(`messages.detail.infra.suurhaigla`)"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4 mb-4">
            <div class="col-span-1">
              <InputText
                fluid
                id="rand"
                v-model="infra.rand"
                :placeholder="$t(`messages.detail.infra.rand`)"
              />
            </div>
            <div class="col-span-1">
              <InputText
                fluid
                id="supluskoht"
                v-model="infra.supluskoht"
                :placeholder="$t(`messages.detail.infra.supluskoht`)"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4 mb-4">
            <div class="col-span-1">
              <InputText
                fluid
                id="ujula"
                v-model="infra.ujula"
                :placeholder="$t(`messages.detail.infra.ujula`)"
              />
            </div>
          </div>
        </template>

        <div class="grid grid-cols-2 gap-4 mb-4">
          <template v-for="(field, key) in fields" :key="key">
            <div class="col-span-1">
              <PredefinedValuesInputComponent
                category="infra"
                :name="field[0]"
                :append-on-select="true"
                v-model="infra[field[0]]"
                :placeholder="$t(`messages.detail.infra.${field[0]}`)"
              />
            </div>
            <div v-if="field[1]" class="col-span-1">
              <PredefinedValuesInputComponent
                category="infra"
                :name="field[1]"
                :append-on-select="true"
                v-model="infra[field[1]]"
                :placeholder="$t(`messages.detail.infra.${field[1]}`)"
              />
            </div>
          </template>
        </div>
      </div>
    </DetailCard>
  </div>
</template>

<style scoped>
  #detail-infra-map {
    .detail-map {
      position: relative;
      height: 600px;
      width: 100%;
      margin: 0;
    }
  }
</style>
